<template>
  <div class="fortune-container">
    <h1 class="fortune-title">{{ yearTitle }}</h1>
    <!-- 최종 결과 도착 전 -->
    <div
      class="fortune-loading"
      v-if="randomStoryText && !storyTypingCompleted"
    >
      <TypingEffect
        :text="randomStoryText"
        @typing-complete="handleStoryTypingComplete"
      />
    </div>
    <!-- 타이핑이 끝났지만 아직 fortune이 없을 때 -->
    <div class="fortune-loading" v-else-if="storyTypingCompleted && !fortune">
      <p class="blinking-text">곧 해석 결과가 도착합니다. 잠시 기다려주세요.</p>
    </div>
    <!-- 최종 결과 도착 -->
    <div class="fortune-content" v-else-if="fortune">
      <p class="fortune-text">{{ fortune }}</p>
    </div>

    <div class="button-area">
      <button @click="goHome">돌아가기</button>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { getAuth } from 'firebase/auth'
  import { getFirestore, doc, getDoc } from 'firebase/firestore'
  import { GraphQLClient, gql } from 'graphql-request'
  import { deductLydiaForMenu } from '@/utils/checkLydia'
  import TypingEffect from '@/components/TypingEffect.vue' // TypingEffect 컴포넌트 임포트
  import { fourPillarsStories } from '@/data/fourPillarsStories' // fourPillarsStories 데이터 임포트
  import { getGanjiByYear } from '@/utils/utils'

  const store = useStore()
  const router = useRouter()

  const yearTitle = ref('')
  const now = new Date()
  const thisYear = now.getFullYear()
  const month = now.getMonth() + 1

  // 11~12월은 미리 내년 운세를 보여줌
  const targetYear = month >= 11 ? thisYear + 1 : thisYear

  const ganji = getGanjiByYear(targetYear)

  yearTitle.value = `${targetYear} ${ganji}년 운세`


  const fortune = ref('') // 최종적으로 표시될 LLM 응답 결과
  const apiResult = ref(null) // LLM 응답 결과를 임시 저장할 변수
  const db = getFirestore()

  // 랜덤 스토리를 저장할 ref 변수
  const randomStoryText = ref('')
  // 랜덤 스토리 타이핑 완료 상태를 추적하는 변수
  const storyTypingCompleted = ref(false)

  // 타이핑 완료 후 딜레이를 위한 타이머 ID
  let typingDelayTimer = null
  let resultCheckTimer = null

  // fourPillarsStories에서 랜덤으로 스토리 선택 및 조합
  const selectRandomStory = () => {
    if (fourPillarsStories && fourPillarsStories.length > 0) {
      const randomIndex = Math.floor(Math.random() * fourPillarsStories.length)
      const selectedStory = fourPillarsStories[randomIndex]

      // 스토리 시작 문구와 선택된 스토리 내용 조합
      randomStoryText.value = `해석을 기다리시는 동안 기초 명리학 상식 하나 배워보세요.\n\n${selectedStory}`
    } else {
      randomStoryText.value = '흥미로운 이야기를 준비 중입니다...' // fourPillarsStories 데이터가 없을 경우 대체 텍스트
    }
    console.log('랜덤 스토리: ', randomStoryText.value)
  }

  // 랜덤 스토리 타이핑 완료 시 호출될 핸들러
  const handleStoryTypingComplete = () => {
    console.log('스토리 타이핑 완료. 0.5초 딜레이 시작.')
    // 타이핑 완료 후 3초 딜레이
    typingDelayTimer = setTimeout(() => {
      storyTypingCompleted.value = true // 3초 후 타이핑 완료 상태 설정
      console.log('0.5초 딜레이 완료. 결과 표시 확인 시작.')

      checkAndDisplayResult()
      // 이후 주기적으로 결과 확인
      if (!resultCheckTimer) {
        resultCheckTimer = setInterval(checkAndDisplayResult, 500) // 0.5초 간격으로 결과 확인
      }
    }, 500)
  }

  // 주기적으로 호출되어 API 결과 도착 시 결과를 표시하는 함수
  const checkAndDisplayResult = () => {
    if (apiResult.value != null) {
      console.log('API 결과 도착 확인. fortune 결과 표시.')
      fortune.value = apiResult.value

      if (resultCheckTimer) {
        clearInterval(resultCheckTimer)
        resultCheckTimer = null // 타이머 ID 초기화
      }
    } else {
      console.log('API 결과 대기 중...')
    }
  }


  // -------------------- 정규화 유틸 --------------------
  function normalizeGender(gender) {
    const s = String(gender ?? '').trim().toLowerCase();
    if (['남', '남성', 'M', 'male'].includes(s)) return '남';
    if (['여', '여성', 'F', 'female'].includes(s)) return '여';
    return ''; // 알 수 없으면 빈 값
  }

  function normalizeIsLunar(userData) {
    // 명시된 isLunar가 우선, 없으면 calendarType 파생, 그래도 없으면 false
    if (typeof userData?.isLunar === 'boolean') return userData.isLunar;
    const ct = String(userData?.calendarType ?? '').trim().toLowerCase();
    if (ct === 'lunar') return true;
    if (ct === 'solar') return false;
    return false;
  }

  function normalizeIsLeapMonth(userData) {
    return typeof userData?.isLeapMonth === 'boolean' ? userData.isLeapMonth : false;
  }

  function normalizeBirthDate(birthDate) {
    const d = String(birthDate ?? '').replace(/\D/g, '');
    return d.length === 8 ? d : '';
  }

  // '03~04', '3-4', '03:00~04:00' 등 → '03~04'
  function normalizeHourRange(rangeLike) {
    if (!rangeLike) return '';
    const parts = String(rangeLike).split(/~|–|—|-|to/i).map(s => s.trim());
    if (parts.length !== 2) return '';

    const parseH = (s) => {
      const m = s.match(/\d{1,2}/);
      if (!m) return null;
      let h = Number(m[0]);
      if (h === 24) h = 0;             // 24시는 00시로 정규화
      if (h < 0 || h > 23) return null;
      return h;
    };

    const a = parseH(parts[0]);
    const b = parseH(parts[1]);
    if (a == null || b == null) return '';

    const A = String(a).padStart(2, '0');
    const B = String(b).padStart(2, '0');
    return `${A}~${B}`;
  }

  // 서버가 HHmm 단일 시각을 요구한다면: 구간의 대표값(시작+30분)으로 변환
  function deriveHHmmFromRange(hourRange) {
    const nr = normalizeHourRange(hourRange);
    if (!nr) return '';

    const [A, B] = nr.split('~').map(Number);

    // 대부분의 계산(시지 배정)은 구간 아무 시각이든 동일 결과 → 시작+30분이 안전
    // 자정 넘김(23~00)도 23:30이면 子시로 정확히 들어감
    const h = A; 
    const m = 30;

    return String(h).padStart(2, '0') + String(m).padStart(2, '0'); // 'HHmm'
  }
  // -------------------- 정규화 유틸 --------------------


  const fetchFortuneResult = async () => {
    try {
      console.log('Fetching gi flow this year..')
      const userData = store.getters.getUser

      if (userData) {
        console.log('user data: ', userData)

        const apiEndpoint = import.meta.env.VITE_API_ENDPOINT
        const client = new GraphQLClient(apiEndpoint)

        const GET_FORTUNE_THIS_YEAR_MUTATION = gql`
          mutation GetFortuneThisYear($input: FortuneInput!) {
            getFortuneThisYear(input: $input) {
              summary
            }
          }
        `
        const variables = {
          input: {
            gender:      normalizeGender(userData.gender),
            birthDate:   normalizeBirthDate(userData.birthDate),
            birthTime:   deriveHHmmFromRange(userData.birthTime), // '0330' 처럼 대표값 전달
            isLunar:     normalizeIsLunar(userData),
            isLeapMonth: normalizeIsLeapMonth(userData),
          }, 
        }

        const data = await client.request(
          GET_FORTUNE_THIS_YEAR_MUTATION,
          variables,
        )
        apiResult.value = data.getFortuneThisYear.summary

        await deductLydiaForMenu('fortuneThisYear', store) // 성공한 경우만 Lydia 차감
      } else {
        console.log('사용자가 데이터가 존재하지 않습니다.')
        apiResult.value = '사용자 데이터가 존재하지 않습니다.'
      }
    } catch (err) {
      apiResult.value = `${yearTitle}를 불러오는데 실패했습니다.`
      console.error(err)
    }
  }

  onMounted(async () => {
    console.log('올해의 운세 user: ', store.getters.getUser)
    
    selectRandomStory() // 컴포넌트 마운트 시 랜덤 스토리 선택 시작
    fetchFortuneResult() // API 호출 시작
  })

  function goHome() {
    router.go(-1)
  }
</script>

<style scoped>
  .fortune-container {
    padding: 1.0rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    box-sizing: border-box;
  }

  .fortune-title {
    font-size: var(--font-size-14);
    text-align: center;
    margin-top: 0.5rem;
    font-weight: bold;
  }

  .fortune-content {
    flex: 1;
    padding: 0.5rem;
    font-size: var(--font-size-10);
    line-height: 1.6; /* 줄 간격 통일 */
    overflow-y: auto;
    white-space: pre-wrap;
    align-self: center;
    width: 90%;
  }

  .fortune-text {
    word-break: break-word;
    text-align: justify; /* 양쪽 정렬 통일 */
    line-height: 1.6; /* 줄 간격 통일 */
    white-space: pre-wrap; /* 마크다운 개행 유지 */
  }

  /* interpretation.vue의 .waiting-interpretation 스타일 적용 */
  .fortune-loading {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    font-size: var(--font-size-10); /* 폰트 사이즈 통일 */
    box-sizing: border-box;
    align-self: center;
    width: 90%;
    display: block; /* flex 컨테이너 대신 block 요소로 변경 또는 flex 속성 조정 */
  }

  /* interpretation.vue의 .waiting-interpretation p 스타일 적용 */
  .fortune-loading :deep(span) {
    /* TypingEffect 컴포넌트의 루트 요소인 span에 스타일 적용 */
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 1.6; /* 줄 간격 통일 */
    text-align: justify; /* 양쪽 정렬 통일 */
    display: block; /* span을 블록 요소처럼 만들어 줄바꿈이 자연스럽게 되도록 */
    width: 100%; /* 부모 요소 너비만큼 확장 */
  }

  .blinking-text {
    animation: blink 2s infinite;
    color: #888;
    margin-top: 1.2rem;
    text-align: center;
  }
  @keyframes blink {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  .button-area {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #f44336;
    color: white;
    cursor: pointer;
  }

  button:hover {
    background-color: #da190b;
  }
</style>
